import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus, KeyRound } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import TeamMemberService from '../services/TeamMemberService';
import RoleService from '../services/RoleService';
import MemberStatusService from '../services/MemberStatusService';

const ROLE_LABELS = { admins: 'Admin', doctors: 'Doctor', nurses: 'Nurse', rsos: 'RSO' };

const EMPTY_FORM = {
  fullname: '',
  email: '',
  mobile: '',
  gender: 'male',
  address: '',
  experience: '',
  emirateId: '',
  roleId: '',
  memberStatusId: '',
  password: '',
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [resetRow, setResetRow] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  const load = async () => {
    try {
      const [members, roleData, statusData] = await Promise.all([
        TeamMemberService.findAll(),
        RoleService.findAll(),
        MemberStatusService.findAll(),
      ]);
      setRows(members.data);
      setRoles(roleData.data);
      setStatuses(statusData.data);
    } catch {
      toast.error('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      ...EMPTY_FORM,
      fullname: row.fullname || '',
      email: row.email || '',
      mobile: row.mobile || '',
      gender: row.gender || 'male',
      address: row.address || '',
      experience: row.experience ?? '',
      emirateId: row.emirateId || '',
      roleId: row.roleId || '',
      memberStatusId: row.memberStatusId || '',
      password: '',
    });
    setEditingId(row.id);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.fullname || !form.email || !form.roleId) {
      toast.error('Full name, email and role are required.');
      return;
    }
    if (!editingId && !form.password) {
      toast.error('A password is required for new users.');
      return;
    }
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    if (payload.experience === '') delete payload.experience;
    if (payload.memberStatusId === '') delete payload.memberStatusId;

    try {
      if (editingId) {
        await TeamMemberService.update(editingId, payload);
        toast.success('User updated.');
      } else {
        await TeamMemberService.create(payload);
        toast.success('User created.');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    try {
      await TeamMemberService.delete(deleteRow.id);
      toast.success('User deleted.');
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    try {
      await TeamMemberService.update(resetRow.id, { password: resetPassword });
      toast.success(`Password reset for ${resetRow.fullname}.`);
      setResetRow(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password reset failed.');
    }
  };

  const headCells = [
    {
      id: 'fullname',
      label: 'User',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800">{row.fullname}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{row.email}</div>
        </div>
      ),
    },
    {
      id: 'role.role_name',
      label: 'Role',
      render: (row) => (
        <Badge variant={row.role?.role_name === 'admins' ? 'red' : 'blue'}>
          {ROLE_LABELS[row.role?.role_name] || row.role?.role_name || '—'}
        </Badge>
      ),
    },
    { id: 'mobile', label: 'Mobile' },
    { id: 'address', label: 'Address' },
    {
      id: 'lastSeenAt',
      label: 'Last Seen',
      render: (row) => (
        <span className="text-neutral-500 text-xs">
          {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : 'Never'}
        </span>
      ),
    },
  ];

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <Sidebar title="User Management">
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-neutral-800">Platform Users</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Create response team users, assign roles and manage credentials. All changes are audit-logged.
            </p>
          </div>
          <Button icon={Plus} onClick={openCreate}>Add User</Button>
        </div>

        <DataTable
          headCells={headCells}
          rows={rows}
          onEdit={openEdit}
          onDelete={(row) => setDeleteRow(row)}
          moreOptions={[
            {
              title: 'Reset Password',
              handler: (row) => {
                setResetRow(row);
                setResetPassword('');
              },
            },
          ]}
          loading={loading}
          emptyLabel={loading ? 'Loading users…' : 'No users found.'}
        />
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Edit User' : 'Add User'}
        subtitle="User accounts control access to the dashboard and mobile app."
        confirmLabel={editingId ? 'Save Changes' : 'Create User'}
        onConfirm={handleSave}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="form-label" htmlFor="u-name">Full Name *</label>
            <input id="u-name" className="form-input" value={form.fullname} onChange={setField('fullname')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-email">Email *</label>
            <input id="u-email" type="email" className="form-input" value={form.email} onChange={setField('email')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-role">Role *</label>
            <select id="u-role" className="form-input" value={form.roleId} onChange={setField('roleId')}>
              <option value="">Select role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{ROLE_LABELS[r.role_name] || r.role_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-status">Availability Status</label>
            <select id="u-status" className="form-input" value={form.memberStatusId} onChange={setField('memberStatusId')}>
              <option value="">Select status…</option>
              {statuses.map((st) => (
                <option key={st.id} value={st.id}>{st.status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-mobile">Mobile</label>
            <input id="u-mobile" className="form-input" value={form.mobile} onChange={setField('mobile')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-gender">Gender</label>
            <select id="u-gender" className="form-input" value={form.gender} onChange={setField('gender')}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-address">Address</label>
            <input id="u-address" className="form-input" value={form.address} onChange={setField('address')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-experience">Experience (years)</label>
            <input id="u-experience" type="number" min="0" className="form-input" value={form.experience} onChange={setField('experience')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-emirateid">Emirates ID</label>
            <input id="u-emirateid" className="form-input" value={form.emirateId} onChange={setField('emirateId')} />
          </div>
          {!editingId && (
            <div>
              <label className="form-label" htmlFor="u-password">Initial Password *</label>
              <input id="u-password" type="password" className="form-input" value={form.password} onChange={setField('password')} />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={Boolean(resetRow)}
        onClose={() => setResetRow(null)}
        title="Reset Password"
        subtitle={resetRow ? `${resetRow.fullname} — ${resetRow.email}` : ''}
        confirmLabel="Reset Password"
        onConfirm={handleResetPassword}
      >
        <div>
          <label className="form-label" htmlFor="u-reset-password">
            <span className="inline-flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> New Password</span>
          </label>
          <input
            id="u-reset-password"
            type="password"
            className="form-input"
            placeholder="Minimum 8 characters"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <p className="text-xs text-neutral-400 mt-2">
            Share the new password securely. The user should change it after first sign-in.
          </p>
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        title="Delete User"
        subtitle={deleteRow ? `${deleteRow.fullname} — ${deleteRow.email}` : ''}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      >
        <p className="text-sm text-neutral-600">
          This permanently removes the user account and revokes access. This action cannot be undone.
        </p>
      </Modal>
    </Sidebar>
  );
}
